/**
 * CX Accordion: WAI-ARIA accordion (button + region). Event delegation.
 * Theme Editor: inactive items use placeholders (see `_cx-accordion-item.liquid`).
 */
(function () {
  const SEL_CONTAINER = '[data-accordion]';
  const SEL_TRIGGER = '[data-accordion-trigger]';

  function getContainers(root) {
    const r = root || document;
    if (!r.querySelectorAll) return [];
    const list = Array.from(r.querySelectorAll(SEL_CONTAINER));
    if (r instanceof Element && r.matches(SEL_CONTAINER) && !list.includes(r)) list.unshift(r);
    return list;
  }

  function getRegion(trigger) {
    const id = trigger.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function setExpanded(trigger, region, expanded) {
    if (!trigger || !region) return;
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    region.toggleAttribute('hidden', !expanded);
    const item = trigger.closest('[data-accordion-item]');
    if (item) item.classList.toggle('accordion__item--open', expanded);
  }

  function enhance(container) {
    if (!(container instanceof HTMLElement)) return;
    if (container.classList.contains('accordion--enhanced')) return;

    const triggers = Array.from(container.querySelectorAll(SEL_TRIGGER)).filter(
      (t) => t instanceof HTMLElement && container.contains(t)
    );
    if (!triggers.length) return;

    const allowMultiple = container.getAttribute('data-accordion-allow-multiple') === 'true';

    container.classList.add('accordion--enhanced');

    triggers.forEach((trigger) => {
      const region = getRegion(trigger);
      if (!region) return;
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      setExpanded(trigger, region, expanded);
    });

    if (!allowMultiple) {
      const openTriggers = triggers.filter((t) => t.getAttribute('aria-expanded') === 'true');
      if (openTriggers.length > 1) {
        openTriggers.slice(1).forEach((t) => {
          const r = getRegion(t);
          if (r) setExpanded(t, r, false);
        });
      }
    }

    container.addEventListener('click', (e) => {
      const trigger = e.target.closest?.(SEL_TRIGGER);
      if (!trigger || !container.contains(trigger)) return;
      const region = getRegion(trigger);
      if (!region || !container.contains(region)) return;

      const currentlyExpanded = trigger.getAttribute('aria-expanded') === 'true';
      const next = !currentlyExpanded;

      if (next && !allowMultiple) {
        triggers.forEach((t) => {
          const r = getRegion(t);
          if (r) setExpanded(t, r, t === trigger);
        });
      } else {
        setExpanded(trigger, region, next);
      }
    });

    container.addEventListener('keydown', (e) => {
      const trigger = e.target.closest?.(SEL_TRIGGER);
      if (!trigger || !container.contains(trigger)) return;
      const idx = triggers.indexOf(trigger);
      if (idx < 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        triggers[(idx + 1) % triggers.length].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        triggers[(idx - 1 + triggers.length) % triggers.length].focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        triggers[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        triggers[triggers.length - 1].focus();
      }
    });
  }

  function init(root) {
    getContainers(root).forEach((el) => enhance(el));
  }

  window.CX = window.CX || {};
  window.CX.initAccordion = init;

  const run = (r) => init(r || document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run(document), { once: true });
  else setTimeout(() => run(document), 0);
  document.addEventListener('shopify:section:load', (e) => run(e?.target instanceof Element ? e.target : document));
  document.addEventListener('shopify:section:reorder', () => run(document));
  document.addEventListener('shopify:block:select', () => run(document));
})();
